import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { DATADOG_CONFIG, I18N_CONFIG, LOAD_MASTER_LIST } from '@conf';
import {
  apiAmcmemberFactory,
  apiAuthorizationFactory,
  apiInitializationFactory,
  apiSysdateFactory,
  apiMemberFactory,
  apiUserFactory,
  apiCreditFactory,
} from '@conf/api.config';
import { environment } from '@env/environment';
import {
  AlertAreaModule,
  AnaBizHeaderModule,
  BasicPageLayoutModule,
  ContentsAreaModule,
  DebugAreaModule,
  EmergencyAreaModule,
  ErrorGuidanceContModule,
  FooterModule,
  GuidanceAreaModule,
  HeaderModule,
  ModalModule,
  NotificationAreaModule,
  PageLoadingModule,
} from '@lib/components';
import { ITranslationResource, MultiTranslateHttpLoader } from '@lib/core';
import { ApiInterceptor, CacheInterceptor } from '@lib/interceptor';
import {
  AswMasterService,
  AswMissingTranslationHandler,
  InitialInfluxService,
  InitialInfluxServiceModule,
  LoggerDatadogServiceModule,
  TealiumService,
  RESTRICTED_NAVIGATION_INITIALIZER_PROVIDER,
  LoggerDatadogService,
} from '@lib/services';
import {
  AMCMemberStoreModule,
  AnaBizContextStoreModule,
  ApiErrorResponseStoreModule,
  AswCommonStoreModule,
  AswContextStoreModule,
  AswMasterStoreModule,
  AswServiceStoreModule,
  DynamicContentStoreModule,
  NotRetryableErrorStoreModule,
  PageLoadingStoreModule,
  RetryableErrorStoreModule,
  SysdateStoreModule,
  storeSyncMetaReducer,
  aswServiceStorageSync,
} from '@lib/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { Action, ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateModuleConfig,
} from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ApiAmcmemberModule } from 'src/sdk-amcmember';
import { ApiAuthorizationModule } from 'src/sdk-authorization';
import { ApiInitializationModule } from 'src/sdk-initialization';
import { ApiSysdateModule } from 'src/sdk-sysdate';
import { ApiUserModule } from 'src/sdk-user';
import { ApiCreditModule } from 'src/sdk-credit';
import { ApiMemberModule } from 'src/sdk-member';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HelloWorldContModule } from './hello-world';
import { SampleSubHeaderModule } from './sub-header/sample-sub-header/sample-sub-header.module';
import { TestModule } from './test';
import { TestSsnModule } from './test-ssn/test-ssn.module';
import { CheckinSearchSubHeaderModule } from './sub-header/checkin-search-sub-header';
import { CheckinCompletedSubHeaderModule } from './sub-header/checkin-completed-sub-header';
import { CheckinSearchServiceModule } from '@common/services/checkin-search/checkin-search.service.module';
import { CheckinMybookingSubHeaderModule } from './sub-header/checkin-mybooking-sub-header/checkin-mybooking-sub-header.module';
import {
  CheckinCommonStoreModule,
  checkinCommonStorageSync,
  CheckinAcceptanceResultStoreModule,
  getFlightsResultStorageSync,
  SearchDecryptionStoreModule,
  FlightAvailabilityToCheckinStoreModule,
  flightAvailabilityToCheckinStorageSync,
} from '@common/store';
import { CheckinSelectSubHeaderModule } from './sub-header/checkin-select-sub-header/checkin-select-sub-header.module';
import { CheckinPassengerInputSubHeaderModule } from './sub-header/checkin-passenger-input-sub-header';
import { CheckinReviewSubHeaderModule } from './sub-header/checkin-review-sub-header';
import { DateFormatModule } from '@lib/pipes';
import { CheckinSeatmapSubHeaderModule } from './sub-header/checkin-seatmap-sub-header';
import { CheckinRouteReuseStrategy } from '@common/strategy/checkin-route-reuse-strategy';
import { apiCheckinFactory, apiServicingFactory } from '@conf';
import { ApiModule as ApiCheckinModule } from 'src/sdk_checkin';
import { ApiModule as ApiServicingModule } from 'src/sdk-servicing';
import { RouteReuseStrategy } from '@angular/router';
import { RESTRICTED_NAVIGATION_SERVICE_CONFIG_PROVIDER } from '@conf';
import { CheckinWaitingNumberSubHeaderModule } from './sub-header/checkin-waiting-number-sub-header/checkin-waiting-number-sub-header.module';
import { CheckinWaitingSearchSubHeaderModule } from './sub-header/checkin-waiting-search-sub-header/checkin-waiting-search-sub-header.module';
import { CheckinStandbyReviewSubHeaderModule } from './sub-header/checkin-standby-review-sub-header';
import { CheckinStandbyFlightAvailabilitySubHeaderModule } from './sub-header/checkin-standby-flight-availability-sub-header/checkin-standby-flight-availability-sub-header.module';
import { CheckinStandbyCompletedSubHeaderModule } from './sub-header/checkin-standby-completed-sub-header';
// 提携：提携では必要ないためコメントアウト
// import { STG_API_INTERCEPTOR } from '@lib/interceptor/stg-api/stg-api-interceptor';

/** `MultiTranslateHttpLoader`用のFactory */
export const MultiTranslateHttpLoaderFactory = (httpClient: HttpClient) => {
  // i18n（国際化対応）Configに定義されたファイルリストをもとにLoader用のFactoryを生成
  const {
    baseUrl,
    app: { cache },
  } = environment.spa;
  const resources = I18N_CONFIG.loadFileList.reduce((previous, current) => {
    if (current.suffix) {
      previous.push({
        prefix: `${baseUrl}${cache}/${current.prefix}`,
        suffix: current.suffix,
      });
    } else {
      previous.push({ prefix: `${baseUrl}${cache}/${current.prefix}` });
    }
    return previous;
  }, [] as ITranslationResource[]);
  return new MultiTranslateHttpLoader(httpClient, resources);
};

/** `TranslateModule.forRoot`用Config */
export const ROOT_NGX_TRANSLATE_MODULE_CONFIG: TranslateModuleConfig = {
  loader: {
    provide: TranslateLoader,
    useFactory: MultiTranslateHttpLoaderFactory,
    deps: [HttpClient],
  },
  missingTranslationHandler: {
    provide: MissingTranslationHandler,
    useClass: AswMissingTranslationHandler,
  },
};

/** `StoreModule.forRoot`用routerReducer */
const rootReducers = {
  router: routerReducer,
};
/** `StoreModule.forRoot`用sessionStorage同期指定  */
const metaReducers = [storageSyncWrapper];
export function storageSyncWrapper<T, V extends Action = Action>(reducer: ActionReducer<T, V>): ActionReducer<T, V> {
  // storeとsessionStorageの同期指定
  const storageStates: any = [
    // FIXME: 各APPごとに指定する
    // 例：{ aswService: aswServiceStorageSync },
    // 共通Storeの中でサービス共通のみセッション永続化の対象であるため追加
    { aswService: aswServiceStorageSync },
    // チェックイン系でセッション永続化の対象であるチェックイン共通を追加
    { checkinCommon: checkinCommonStorageSync },
    // フライトリスト取得APIのレスポンスストアの永続化
    { getFlightsResult: getFlightsResultStorageSync },
    // 空席照会結果からの遷移情報用ストアの永続化
    { flightAvailabilityToCheckin: flightAvailabilityToCheckinStorageSync },
  ];
  return storeSyncMetaReducer({ keys: storageStates, rehydrate: true, storage: sessionStorage || localStorage })(
    reducer
  );
}

/** デフォルトの初期化処理（一番最初に実行する処理） */
function defaultInitializer(tealiumSvc: TealiumService, loggerSvc: LoggerDatadogService) {
  return async () => {
    // Datadogブラウザログの初期化
    loggerSvc.init();
    // Tealium連携用Global変数の初期化
    tealiumSvc.init();
    // Tealium用JavaScriptロード
    return lastValueFrom(tealiumSvc.loadScript$());
  };
}

// 初期流入処理
function initialize(service: InitialInfluxService, masterService: AswMasterService) {
  return async () => {
    await service.exe();
    await lastValueFrom(masterService.load(LOAD_MASTER_LIST));
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    EffectsModule.forRoot(),
    AswCommonStoreModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ApiCheckinModule.forRoot(apiCheckinFactory),
    ApiServicingModule.forRoot(apiServicingFactory),
    ApiSysdateModule.forRoot(apiSysdateFactory),
    TranslateModule.forRoot(ROOT_NGX_TRANSLATE_MODULE_CONFIG),
    StoreModule.forRoot(rootReducers, { metaReducers }),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.envName !== 'prd',
    }),
    StoreRouterConnectingModule.forRoot(),
    // [start] テンプレート用サンプルcomponent
    HelloWorldContModule,
    TestModule,
    TestSsnModule,
    SampleSubHeaderModule,
    // [end] テンプレート用サンプルcomponent
    BasicPageLayoutModule,
    ContentsAreaModule,
    EmergencyAreaModule,
    HeaderModule,
    AnaBizHeaderModule,
    AlertAreaModule,
    GuidanceAreaModule,
    FooterModule,
    DebugAreaModule,
    NotificationAreaModule,
    ModalModule,
    InitialInfluxServiceModule,
    ErrorGuidanceContModule,
    PageLoadingModule,
    // [start] 共通ストア系
    SysdateStoreModule,
    AswServiceStoreModule,
    AswContextStoreModule,
    AswCommonStoreModule,
    AnaBizContextStoreModule,
    AMCMemberStoreModule,
    AswMasterStoreModule,
    ApiErrorResponseStoreModule,
    RetryableErrorStoreModule,
    NotRetryableErrorStoreModule,
    DynamicContentStoreModule,
    PageLoadingStoreModule,
    // [end] 共通ストア系
    LoggerDatadogServiceModule.forRoot(() => DATADOG_CONFIG),
    // [start] ApiModule
    ApiAmcmemberModule.forRoot(apiAmcmemberFactory),
    ApiAuthorizationModule.forRoot(apiAuthorizationFactory),
    ApiInitializationModule.forRoot(apiInitializationFactory),
    ApiMemberModule.forRoot(apiMemberFactory),
    ApiUserModule.forRoot(apiUserFactory),
    ApiCreditModule.forRoot(apiCreditFactory),
    ApiSysdateModule.forRoot(apiSysdateFactory),
    // [end] ApiModule
    CheckinSearchSubHeaderModule,
    CheckinCompletedSubHeaderModule,
    CheckinSearchServiceModule,
    CheckinCommonStoreModule,
    CheckinAcceptanceResultStoreModule,
    CheckinMybookingSubHeaderModule,
    CheckinSelectSubHeaderModule,
    CheckinPassengerInputSubHeaderModule,
    CheckinReviewSubHeaderModule,
    CheckinSeatmapSubHeaderModule,
    CheckinWaitingNumberSubHeaderModule,
    CheckinWaitingSearchSubHeaderModule,
    CheckinStandbyReviewSubHeaderModule,
    CheckinStandbyCompletedSubHeaderModule,
    DateFormatModule,
    SearchDecryptionStoreModule,
    CheckinStandbyFlightAvailabilitySubHeaderModule,
    FlightAvailabilityToCheckinStoreModule,
  ],
  providers: [
    // API接続Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    // Akamai Cache接続Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    // STG API切替Interceptor
    // 提携：提携では必要ないためコメントアウト
    // STG_API_INTERCEPTOR,
    // デフォルトの初期化処理
    {
      provide: APP_INITIALIZER,
      useFactory: defaultInitializer,
      deps: [TealiumService, LoggerDatadogService],
      multi: true,
    },
    // 初期流入
    {
      provide: APP_INITIALIZER,
      useFactory: initialize,
      deps: [InitialInfluxService, AswMasterService, Router],
      multi: true,
    },
    // チェックイン独自：コンポーネントの再利用ルール
    { provide: RouteReuseStrategy, useClass: CheckinRouteReuseStrategy },
    // G03_共通部品_共通・汎用処理（520_ブラウザバック・直リンク・F5制御機能）
    RESTRICTED_NAVIGATION_SERVICE_CONFIG_PROVIDER,
    RESTRICTED_NAVIGATION_INITIALIZER_PROVIDER,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
